import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import {
  pageCount,
  type AppConfigDto,
  type BaseDriversResponseDTO,
  type CallSortBy,
  type ImpoundSortBy,
  type InvoiceResponseDTO,
  type LocationResponseDto,
  type OnboardingResponseDto,
  type Paginated,
  type PlanCatalogueDto,
  type PlanUsageDto,
  type TaskResponseDTO,
  type UpdateUserDto,
  type UpdateVehicleDto,
  type UserAccessDto,
} from '@relay-towing/api';
import { turnstileHeaders } from '@relay-towing/shared-website-components';
import type {
  onboardingRequestBody,
  VehicleHistoryRow,
} from '@relay-towing/ui';
import env from '../config/env';
import { listPath } from '../api/list';
import { getDriverTokenManager } from '../utils/tokenManager';
import { isEmbeddedMode } from '../utils/embeddedMode';
import { SELECTED_COMPANY_KEY } from '../hooks/userCompanies.shared';
import { devLog } from '../utils/devLog';
import { isShapedAuthError } from '../utils/apiAuthError';

// Keep the HTTP status on a failed call fetch. The interceptor already shapes
// a 401 and 403; a 404 (deleted or foreign call) and the rest used to collapse
// into a bare message, so the call screen could only say "failed".
const toCallError = (error: any, fallback: string): Error => {
  if (isShapedAuthError(error)) return error;
  const shaped = new Error(error.response?.data?.message || fallback);
  if (typeof error.response?.status === 'number') {
    (shaped as any).status = error.response.status;
  }
  return shaped;
};

// Driver Profile API
export interface DriverProfile {
  id: string;
  userId: string; // User ID for permissions lookup
  name: string;
  email: string;
  phone: string;
  status: 'ACTIVE' | 'ON_LEAVE';
  truckNumber: string;
  employeeNumber: string;
  startDate: string;
  locationLatitude: number;
  locationLongitude: number;
  companyId: string; // Add companyId field
}

// Driver Call API
export interface DriverCall {
  id: string;
  dispatchNumber: string;
  type: 'NEW' | 'COMPLETED' | 'SCHEDULED';
  status:
    | 'NEW'
    | 'PENDING'
    | 'DISPATCHED'
    | 'ON_SCENE'
    | 'TOWING'
    | 'COMPLETED'
    | 'FAILED'
    | 'CANCELED';
  contactName: string;
  contactPhone: string;
  pickupLocation: string;
  pickupLocationLat: number;
  pickupLocationLong: number;
  destination: string;
  destinationLat: number;
  destinationLong: number;
  priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
  account: 'CITY_POLICE_DEPARTMENT' | 'PRIVATE_PROPERTY' | 'HIGHWAY_PATROL';
  scheduledAt: string | null;
  notes: string | null;
  completionTime: string | null;
  createdAt: string;
  startedAt: string | null;
  arrivedAt: string | null;
  source: 'MANUAL' | 'CHAT' | 'SCHEDULED' | 'URGENTLY' | 'EXTERNAL_API';
  sourceMetadata: Record<string, any> | null;
  drivers?: Array<{
    id: string;
    name: string;
    email: string;
    truckNumber: string | null;
  }>;
  vehicle: {
    id: string;
    licensePlate: string;
    vin: string;
    vehicleType: string;
    vehicleName: string | null;
    vehicleModel: string | null;
    vehicleYear: number | null;
    vehicleAttributes: {
      color?: string;
      [key: string]: any;
    } | null;
    odometer: number | null;
    vehicleImages: string[];
  };
}

// Location Update API
export interface LocationUpdate {
  latitude: number;
  longitude: number;
}

// Driver Impound API - matching backend structure
export interface DriverImpound {
  id: string;
  impoundNumber: string;
  dispatchId: string;
  invoiceId: string | null;
  driverId: string | null;
  companyId: string;
  locationId: string | null;
  impoundDate: string;
  daysHeld: number;
  total: number;
  balanceDue: number;
  storageLot: string;
  status: 'ACTIVE' | 'RELEASED';
  /** When the vehicle was released (API: releasedDate) */
  releasedDate?: string | null;
  createdAt: string;
  updatedAt: string;
  dispatch: {
    id: string;
    dispatchNumber: string;
    type: string;
    contactName: string;
    contactPhone: string;
    pickupLocation: string;
    destination: string;
    priority: 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';
    account: string;
    notes: string | null;
    vehicle: {
      id: string;
      licensePlate: string;
      vin: string;
      vehicleType: string;
      vehicleName: string | null;
      vehicleModel: string | null;
      vehicleYear: number | null;
      vehicleAttributes: {
        color?: string;
        mileage?: number;
        [key: string]: any;
      } | null;
      odometer: number | null;
      vehicleImages: string[];
    };
  } | null;
  driver: {
    id: string;
    name: string;
    email: string;
    phone: string;
    truckNumber: string;
  } | null;
  invoice: {
    id: string;
    invoiceNumber: string;
    total: number;
    balanceDue: number;
  } | null;
}

// Driver Tasks API - matching backend structure
export interface DriverTask {
  id: string;
  taskTitle: string;
  driverId: string;
  companyId: string;
  dueDate?: Date;
  assignedToId: string;
  status: TaskStatus;
  priority: number;
  description?: string;
  notes?: any;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
  isRepeat: boolean;
  repeatInterval?: 'DAYS' | 'WEEKS' | 'MONTHS';
  repeatDuration?: number;
  /** Whose the task is; the task list includes it. */
  driver?: { id: string; name: string };
}

export enum TaskStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// What useDriverTasks caches: every page's tasks, without the meta.
export type DriverTasksArrayResponse = DriverTask[];

/**
 * GET /drivers/:id. The generated type names only the id, name and phone;
 * the response carries these too.
 */
export type DriverDetail = BaseDriversResponseDTO & {
  email?: string | null;
  status: string;
  truckNumber?: string | null;
};

/** Per-request company context. Empty when unknown, so the interceptor falls
 * back to the stored selection, if any. */
const companyHeader = (companyId?: string): Record<string, string> =>
  companyId ? { 'x-company-id': companyId } : {};

class DriverApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: env.apiUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Attach a fresh token before every request via TokenManager
    this.client.interceptors.request.use(
      async (config) => {
        const token = await this.resolveToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Fill in the company from the stored selection when the caller did
        // not set one. An explicit header wins: it comes from the profile and
        // is there before the selection is stored on a fresh login.
        const selectedCompanyId = localStorage.getItem(SELECTED_COMPANY_KEY);
        if (selectedCompanyId && !config.headers['x-company-id']) {
          config.headers['x-company-id'] = selectedCompanyId;
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: 401 retry with token refresh, 403 passthrough
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const originalRequest = error.config as AxiosRequestConfig & {
            _retried?: boolean;
          };

          if (!originalRequest._retried) {
            originalRequest._retried = true;
            devLog(
              '[DriverApi] 401 received, attempting token refresh and retry'
            );

            const rejectedToken = String(
              originalRequest.headers?.Authorization ?? ''
            ).replace(/^Bearer /, '');

            const newToken = await getDriverTokenManager().forceRefresh();

            // Retry only with a genuinely different token. When the underlying
            // session is dead the refresh resolves with the same string, and
            // replaying the request is a guaranteed second 401 — that doubled
            // every failure in production. TokenManager already dedupes
            // concurrent refreshes, so this is the remaining amplifier.
            if (newToken && newToken !== rejectedToken) {
              devLog('[DriverApi] Token refreshed, retrying request');
              originalRequest.headers = originalRequest.headers ?? {};
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return this.client.request(originalRequest);
            }

            devLog('[DriverApi] Refresh returned no new token, not retrying');
          }

          console.error('Unauthorized access:', error.response?.data?.message);
          const unauthorizedError = new Error(
            error.response?.data?.message || 'Unauthorized access'
          );
          (unauthorizedError as any).status = 401;
          throw unauthorizedError;
        }

        if (error.response?.status === 403) {
          console.error(
            'Forbidden access - no company found:',
            error.response?.data?.message
          );
          const forbiddenError = new Error(
            error.response?.data?.message || 'No company found for your user'
          );
          (forbiddenError as any).status = 403;
          throw forbiddenError;
        }

        return Promise.reject(error);
      }
    );
  }

  /**
   * Resolve a token from the TokenManager, falling back to localStorage
   * for embedded mode when the manager has not been initialised yet.
   */
  private async resolveToken(): Promise<string | null> {
    const token = await getDriverTokenManager().getToken();
    if (token) return token;

    if (isEmbeddedMode()) {
      return localStorage.getItem('access_token');
    }
    return null;
  }

  /**
   * Directly set the auth token on the axios defaults.
   * Kept for backward compatibility; also updates the TokenManager cache.
   */
  setAuthToken(token: string) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    getDriverTokenManager().updateCachedToken(token);
  }

  // Clear auth token
  clearAuthToken() {
    delete this.client.defaults.headers.common['Authorization'];
  }

  // Get driver profile
  async getProfile(): Promise<DriverProfile> {
    try {
      const response = await this.client.get('/driver-mobile/profile');
      return response.data;
    } catch (error: any) {
      console.error('[DriverApi] Profile API failed:', error.message);

      // If the error is already from our response interceptor (401 unauthorized),
      // re-throw it directly to preserve the server message
      if (error.message && error.message !== 'Failed to get driver profile') {
        throw error;
      }
      // For other errors, check response message or use fallback
      throw new Error(
        error.response?.data?.message || 'Failed to get driver profile'
      );
    }
  }

  /**
   * The caller's board: calls assigned to them that are live today, most
   * urgent first. The shared /calls list with the filters that used to be the
   * whole of /driver-mobile/calls.
   *
   * The caller passes the company from the profile: the interceptor's stored
   * selection is empty for a moment after a fresh login.
   */
  async getCalls(companyId?: string): Promise<Paginated<DriverCall>> {
    try {
      const response = await this.client.get(
        listPath('/calls', {
          assignedToMe: true,
          view: 'today',
          sortBy: 'board' satisfies CallSortBy,
          pageSize: 100,
        }),
        { headers: companyHeader(companyId) }
      );
      return response.data as Paginated<DriverCall>;
    } catch (error: any) {
      // A 401 or 403 has already been shaped by the response interceptor and
      // carries a status. Wrapping it again would drop that, and the UI needs
      // it to tell an expired session from a missing company link.
      if (isShapedAuthError(error)) throw error;
      throw new Error(
        error.response?.data?.message || 'Failed to get driver calls'
      );
    }
  }

  /**
   * One call, for any role. The backend decides whether the caller may see
   * it; owners and drivers used to read it from two different endpoints.
   */
  async getCall(callId: string, companyId?: string): Promise<DriverCall> {
    try {
      const response = await this.client.get(`/calls/${callId}`, {
        headers: companyHeader(companyId),
      });
      return response.data;
    } catch (error: any) {
      throw toCallError(error, 'Failed to get call');
    }
  }

  /**
   * Take an outside call on offer, with this member as its driver. The
   * server allows it with "Accept outside calls" only for the caller's own
   * Driver row, and answers 409 when another accept got there first; the
   * status is kept so the call screen can say which.
   */
  async acceptOutsideCall(
    callId: string,
    driverId: string
  ): Promise<{ success: boolean; message: string; dispatch: DriverCall }> {
    try {
      const response = await this.client.put(
        `/dispatch-offers/${callId}/accept`,
        undefined,
        { params: { driverId } }
      );
      return response.data;
    } catch (error: any) {
      throw toCallError(error, 'The call could not be accepted');
    }
  }

  // Get driver impounds
  async getImpounds(
    params?: {
      status?: 'ACTIVE' | 'RELEASED' | 'ALL';
      sortBy?: ImpoundSortBy;
      pageNo?: number;
      pageSize?: number;
      searchQuery?: string;
    },
    companyId?: string
  ): Promise<Paginated<DriverImpound>> {
    try {
      // Falsy values are left out, as the hand-built query string always did:
      // an empty search or a pageNo of 0 was never sent.
      const url = listPath('/impounds', {
        status:
          params?.status && params.status !== 'ALL' ? params.status : undefined,
        sortBy: params?.sortBy || undefined,
        pageNo: params?.pageNo || undefined,
        pageSize: params?.pageSize || undefined,
        searchQuery: params?.searchQuery || undefined,
      });

      devLog('🚛 [Driver API] Fetching impounds with params:', params);
      devLog('🚛 [Driver API] Company ID:', companyId);

      devLog('🚛 [Driver API] Request URL:', url);

      const headers: any = {};
      if (companyId) {
        headers['x-company-id'] = companyId;
      }

      const response = await this.client.get(url, { headers });
      const page = response.data as Paginated<DriverImpound>;

      devLog('🚛 [Driver API] Impounds response:', {
        dataLength: page.data.length,
        totalCount: page.meta.totalCount,
      });

      return page;
    } catch (error: any) {
      console.error('🚛 [Driver API] Failed to get impounds:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data,
        fullError: error,
      });
      throw new Error(
        error.response?.data?.message || 'Failed to get driver impounds'
      );
    }
  }

  // Get count of released impounds today
  async getReleasedImpoundsCount(companyId?: string): Promise<number> {
    try {
      const headers: any = {};
      if (companyId) {
        headers['x-company-id'] = companyId;
      }

      devLog('🚛 [Driver API] Fetching released impounds count');
      devLog('🚛 [Driver API] Company ID:', companyId);

      const response = await this.client.get('/impounds/stats/released-today', {
        headers,
      });

      devLog(
        '🚛 [Driver API] Released impounds count response:',
        response.data
      );

      return response.data;
    } catch (error: any) {
      console.error(
        '🚛 [Driver API] Failed to get released impounds count:',
        error
      );
      throw new Error(
        error.response?.data?.message || 'Failed to get released impounds count'
      );
    }
  }

  /**
   * Every task assigned to the driver: the shared task list, filtered to
   * them, read page by page. It asked for no page, so the default of ten
   * applied and an eleventh task never reached the driver.
   */
  async getTasks(driverId: string, companyId?: string): Promise<DriverTask[]> {
    try {
      devLog('📋 [Driver API] Fetching tasks for driver:', driverId);
      devLog('📋 [Driver API] Company ID:', companyId);

      const headers: any = {};
      if (companyId) {
        headers['x-company-id'] = companyId;
      }

      const tasks: DriverTask[] = [];
      for (let pageNo = 1; ; pageNo++) {
        const response = await this.client.get(
          listPath('/tasks', { driverId, pageNo, pageSize: 100 }),
          { headers }
        );
        const page = response.data as Paginated<DriverTask>;
        tasks.push(...page.data);
        if (pageNo >= pageCount(page.meta)) break;
      }

      devLog('📋 [Driver API] Tasks response:', { dataLength: tasks.length });

      return tasks;
    } catch (error: any) {
      console.error('📋 [Driver API] Failed to get tasks:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data,
        fullError: error,
      });
      // Same as getCalls: keep the interceptor's status on a 401 or 403.
      if (isShapedAuthError(error)) throw error;
      throw new Error(
        error.response?.data?.message || 'Failed to get driver tasks'
      );
    }
  }

  // Complete a task
  async completeTask(
    taskId: string,
    companyId?: string,
    completionNotes?: string
  ): Promise<{ success: boolean; message: string; task: DriverTask }> {
    try {
      devLog('📋 [Driver API] Completing task:', taskId);
      devLog('📋 [Driver API] Company ID:', companyId);

      const headers: any = {};
      if (companyId) {
        headers['x-company-id'] = companyId;
      }

      const body = {};

      const response = await this.client.post(
        `/tasks/${taskId}/complete`,
        body,
        { headers }
      );

      devLog('📋 [Driver API] Task completion response:', {
        status: response.status,
        data: response.data,
      });

      return response.data;
    } catch (error: any) {
      console.error('📋 [Driver API] Failed to complete task:', {
        status: error.response?.status,
        message: error.response?.data?.message,
        data: error.response?.data,
        fullError: error,
      });
      throw new Error(
        error.response?.data?.message || 'Failed to complete task'
      );
    }
  }

  // Get single impound details
  async getImpound(
    impoundId: string,
    companyId?: string
  ): Promise<DriverImpound> {
    try {
      const headers: any = {};
      if (companyId) {
        headers['x-company-id'] = companyId;
      }

      const response = await this.client.get(`/impounds/${impoundId}`, {
        headers,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to get driver impound'
      );
    }
  }

  // Update driver location
  async updateLocation(
    location: LocationUpdate,
    companyId?: string
  ): Promise<{ message: string }> {
    try {
      const response = await this.client.patch(
        '/driver-mobile/location',
        location,
        { headers: companyHeader(companyId) }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update location'
      );
    }
  }

  // Update vehicle VIN
  async updateVehicleVin(
    vehicleId: string,
    vin: string,
    additionalData?: {
      vehicleName?: string;
      vehicleModel?: string;
      vehicleYear?: number;
    }
  ): Promise<{ success: boolean; message: string; vehicle: any }> {
    // The vehicle's own update route, which also records history; the VIN
    // route that only set the VIN has folded into it.
    return this.updateVehicle(vehicleId, { vin, ...additionalData });
  }

  // Update vehicle information
  async updateVehicle(
    vehicleId: string,
    updateData: UpdateVehicleDto
  ): Promise<{ success: boolean; message: string; vehicle: any }> {
    try {
      devLog(
        '🌐 [DriverApi] Making PUT request to:',
        `/calls/vehicle/${vehicleId}`
      );
      devLog('🌐 [DriverApi] Request data:', updateData);
      devLog(
        '🌐 [DriverApi] Auth token set:',
        !!this.client.defaults.headers.common['Authorization']
      );

      const response = await this.client.put(
        `/calls/vehicle/${vehicleId}`,
        updateData
      );

      devLog('🌐 [DriverApi] Response status:', response.status);
      devLog('🌐 [DriverApi] Response data:', response.data);

      return response.data;
    } catch (error: any) {
      console.error('🌐 [DriverApi] Update vehicle error:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
      });
      throw new Error(
        error.response?.data?.message || 'Failed to update vehicle'
      );
    }
  }

  // Upload vehicle media (image or video)
  async uploadVehicleMedia({
    vehicleId,
    file,
    documentType,
    onUploadProgress,
  }: {
    vehicleId: string;
    file: File;
    documentType?: string;
    onUploadProgress?: (progress: number) => void;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vehicleId', vehicleId);
      if (documentType) {
        formData.append('documentType', documentType);
      }

      const response = await this.client.post(
        '/calls/vehicle/media',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-source': 'DRIVER_APP',
          },
          timeout: 300000, // 5 minutes for video uploads
          onUploadProgress: (e) => {
            if (e.total)
              onUploadProgress?.(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      return response.data;
    } catch (error: any) {
      // A 403 arrives shaped, with the server's words: a video on a plan
      // without video says which plan has it.
      if (isShapedAuthError(error)) throw error;
      throw new Error(
        error.response?.data?.message || 'Failed to upload vehicle media'
      );
    }
  }

  // Upload vehicle image
  async uploadVehicleImage({
    vehicleId,
    file,
    documentType,
    onUploadProgress,
  }: {
    vehicleId: string;
    file: File;
    documentType?: string;
    onUploadProgress?: (progress: number) => void;
  }): Promise<any> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vehicleId', vehicleId);
      if (documentType) {
        formData.append('documentType', documentType);
      }

      const response = await this.client.post(
        '/calls/vehicle/image',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-source': 'DRIVER_APP',
          },
          timeout: 120000, // 2 minutes for image uploads
          onUploadProgress: (e) => {
            if (e.total)
              onUploadProgress?.(Math.round((e.loaded / e.total) * 100));
          },
        }
      );

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to upload vehicle image'
      );
    }
  }

  // Delete vehicle image
  async deleteVehicleImage(vehicleId: string, imageUrl: string): Promise<any> {
    try {
      const response = await this.client.delete('/calls/vehicle/image', {
        data: { vehicleId, imageUrl },
        headers: {
          'x-source': 'DRIVER_APP',
        },
      });

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to delete vehicle image'
      );
    }
  }

  // Update dispatch status
  async updateDispatchStatus(
    dispatchId: string,
    status: string,
    version?: number,
    additionalInfo?: Record<string, any>
  ): Promise<{
    success: boolean;
    message: string;
    dispatch: any;
    currentVersion?: number;
  }> {
    try {
      const payload: any = { status, version };
      if (additionalInfo) {
        payload.additionalInfo = additionalInfo;
      }
      // The shared status route; the header marks the change as the driver's
      // for vehicle history and for what Urgently is told.
      const response = await this.client.patch(
        `/calls/${dispatchId}/status`,
        payload,
        { headers: { 'x-source': 'DRIVER_APP' } }
      );
      return response.data;
    } catch (error: any) {
      // Check if it's a version conflict error
      if (error.response?.status === 409) {
        // Extract and return the current version
        const currentVersion = error.response?.data?.currentVersion;
        throw new Error(
          `Version conflict: The call has been updated by someone else. Please try again.`
        );
      }
      throw new Error(
        error.response?.data?.message || 'Failed to update dispatch status'
      );
    }
  }

  // Report dispatch GOA (Gone On Arrival)
  async reportDispatchGoa(
    dispatchId: string,
    goaReason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string; dispatch: any }> {
    try {
      const payload: any = { goaReason };
      if (notes) {
        payload.notes = notes;
      }
      const response = await this.client.put(
        `/calls/${dispatchId}/goa?originator=DRIVER`,
        payload
      );
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to report GOA');
    }
  }

  // Cancel Urgently call
  async cancelUrgentlyCall(
    dispatchId: string,
    companyId: string,
    cancelReason: string,
    notes?: string
  ): Promise<{ success: boolean; message: string; dispatch: any }> {
    try {
      const payload: any = { cancelReason };
      if (notes) {
        payload.notes = notes;
      }
      const response = await this.client.put(
        `/calls/${dispatchId}/cancel?companyId=${companyId}&originator=DRIVER`,
        payload
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to cancel Urgently call'
      );
    }
  }

  // Update driver status
  async updateDriverStatus(
    status: string
  ): Promise<{ success: boolean; message: string; driver: any }> {
    try {
      const response = await this.client.put('/driver-mobile/status', {
        status,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update driver status'
      );
    }
  }

  // Update call status
  async updateCallStatus(
    callId: string,
    status: string,
    version?: number
  ): Promise<any> {
    try {
      const response = await this.client.patch(
        `/calls/${callId}/status`,
        { status, version },
        { headers: { 'x-source': 'DRIVER_APP' } }
      );
      return response.data;
    } catch (error: any) {
      // Check if it's a version conflict error
      if (error.response?.status === 409) {
        // Extract and return the current version
        const currentVersion = error.response?.data?.currentVersion;
        throw new Error(
          `Version conflict: The call has been updated by someone else. Please try again.`
        );
      }
      throw new Error(
        error.response?.data?.message || 'Failed to update call status'
      );
    }
  }

  // Get vehicle images
  async getVehicleImages(vehicleId: string): Promise<string[]> {
    try {
      const response = await this.client.get(
        `/calls/vehicle/${vehicleId}/images`
      );
      return response.data.images;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to get vehicle images'
      );
    }
  }

  // Create impound from a dispatch
  async createImpound(data: {
    dispatchId: string;
    driverId?: string;
    /** The lot it goes into; the server prefers it to storageLot. */
    locationId?: string;
    storageLot: string;
    status?: 'ACTIVE' | 'RELEASED';
    companyId: string; // Add companyId parameter
  }): Promise<DriverImpound> {
    try {
      // Extract companyId from data and add it to headers
      const { companyId, ...impoundData } = data;

      const response = await this.client.post(
        '/impounds',
        {
          ...impoundData,
          status: impoundData.status || 'ACTIVE', // Ensure status is always set
        },
        {
          headers: {
            'x-company-id': companyId,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to create impound'
      );
    }
  }

  // The vehicle's history, newest first: a snapshot per change, which the
  // Call activity sheet diffs into what changed. Any member of the company.
  async getVehicleHistory(vehicleId: string): Promise<VehicleHistoryRow[]> {
    try {
      const response = await this.client.get(
        `/vehicle-history/vehicle/${vehicleId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to get vehicle history'
      );
    }
  }

  // Vehicle Notes API methods
  async getVehicleNotes(
    vehicleId: string
  ): Promise<{ notes: any[]; total: number }> {
    try {
      const response = await this.client.get(
        `/vehicle-notes/vehicle/${vehicleId}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to get vehicle notes'
      );
    }
  }

  async addVehicleNote(
    vehicleId: string,
    noteData: { noteText: string }
  ): Promise<any> {
    try {
      const response = await this.client.post(
        `/vehicle-notes/vehicle/${vehicleId}`,
        noteData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to add vehicle note'
      );
    }
  }

  async updateVehicleNote(
    noteId: string,
    noteData: { noteText: string }
  ): Promise<any> {
    try {
      const response = await this.client.put(
        `/vehicle-notes/${noteId}`,
        noteData
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update vehicle note'
      );
    }
  }

  async deleteVehicleNote(noteId: string): Promise<void> {
    try {
      await this.client.delete(`/vehicle-notes/${noteId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to delete vehicle note'
      );
    }
  }

  // Vehicle Media Comments API methods
  async getVehicleMediaComments(
    vehicleId: string,
    mediaUrl: string
  ): Promise<{ comments: any[]; total: number }> {
    try {
      const response = await this.client.get(
        `/vehicle-media-comments/vehicle/${vehicleId}`,
        {
          params: { mediaUrl },
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to get media comments'
      );
    }
  }

  async addVehicleMediaComment(
    vehicleId: string,
    data: { mediaUrl: string; text: string }
  ): Promise<any> {
    try {
      const response = await this.client.post(
        `/vehicle-media-comments/vehicle/${vehicleId}`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to add media comment'
      );
    }
  }

  async updateVehicleMediaComment(
    commentId: string,
    text: string
  ): Promise<any> {
    try {
      const response = await this.client.put(
        `/vehicle-media-comments/${commentId}`,
        { text }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to update media comment'
      );
    }
  }

  async deleteVehicleMediaComment(commentId: string): Promise<void> {
    try {
      await this.client.delete(`/vehicle-media-comments/${commentId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to delete media comment'
      );
    }
  }

  /** Every plan, what it includes and allows: GET /plans. */
  async getPlanCatalogue(): Promise<PlanCatalogueDto> {
    const response = await this.client.get('/plans');
    return response.data;
  }

  /** The company's plan and how much of each limit it has used. */
  async getPlanUsage(companyId: string): Promise<PlanUsageDto> {
    const response = await this.client.get('/plans/usage', {
      headers: { 'x-company-id': companyId },
    });
    return response.data;
  }

  async getPermissions(
    userId: string,
    companyId: string
  ): Promise<UserAccessDto> {
    // Errors propagate. Returning empty here was cached as a real answer:
    // one failed request read as "you hold no permissions".
    const response = await this.client.get(`/permissions/user/${userId}`, {
      headers: { 'x-company-id': companyId },
    });
    return { permissions: response.data.permissions || [] };
  }

  /**
   * Whether an email is free to sign up with: `GET /onboarding/check-email`.
   * Public, asked before any sign-in.
   */
  async checkSignUpEmail(email: string): Promise<{ available: boolean }> {
    const response = await this.client.get('/onboarding/check-email', {
      params: { email },
    });
    return response.data;
  }

  /**
   * Create the account, its company and its owner: `POST
   * /onboarding/register`, the dashboard's sign-up route. Public and behind
   * the Turnstile bot check, so the token goes with it.
   */
  async register(
    body: ReturnType<typeof onboardingRequestBody>,
    turnstileToken?: string
  ): Promise<OnboardingResponseDto> {
    const response = await this.client.post('/onboarding/register', body, {
      headers: turnstileHeaders(turnstileToken),
    });
    return response.data;
  }

  /**
   * What the server tells every app on launch: today, the oldest wrapper
   * build it still serves. Public, so a build too old to sign in is told
   * before it tries.
   */
  async getAppConfig(): Promise<AppConfigDto> {
    const response = await this.client.get('/app/config');
    return response.data;
  }

  /** One task, for any role; the server decides whether the caller may see it. */
  async getTask(taskId: string): Promise<TaskResponseDTO> {
    try {
      const response = await this.client.get(`/tasks/${taskId}`);
      return response.data;
    } catch (error: any) {
      throw toCallError(error, 'Failed to get task');
    }
  }

  /** One invoice, for any role; the server decides whether the caller may see it. */
  async getInvoice(invoiceId: string): Promise<InvoiceResponseDTO> {
    try {
      const response = await this.client.get(`/invoices/${invoiceId}`);
      return response.data;
    } catch (error: any) {
      throw toCallError(error, 'Failed to get invoice');
    }
  }

  /**
   * One driver of the caller's company, for the drivers screen: their name,
   * phone, email, status and truck. Null for one in another company.
   */
  async getDriver(driverId: string): Promise<DriverDetail | null> {
    try {
      const response = await this.client.get(`/drivers/${driverId}`);
      return response.data;
    } catch (error: any) {
      throw toCallError(error, 'Failed to get driver');
    }
  }

  /**
   * Delete the caller's own account: their login, their memberships, and
   * their name on past work. The server refuses an owner with 409 until they
   * hand the company to someone else or cancel it; the error keeps that
   * status so the settings screen can say so.
   */
  /** The company's storage lots, which any member may list. */
  async getLocations(): Promise<LocationResponseDto[]> {
    const response = await this.client.get('/locations');
    return response.data;
  }

  /** Your own name, on your account and every company's driver record. */
  async updateProfile(body: UpdateUserDto): Promise<void> {
    try {
      await this.client.put('/auth/me', body);
    } catch (error: any) {
      throw toCallError(error, 'Your name could not be saved');
    }
  }

  async deleteAccount(): Promise<void> {
    try {
      await this.client.delete('/auth/me');
    } catch (error: any) {
      throw toCallError(error, 'Your account could not be deleted');
    }
  }

  // Submit inspection report with photos
  async submitInspectionReport(
    dispatchId: string,
    data: {
      inspectionType: 'ON_SITE' | 'ON_DROP_OFF';
      vehicleDamaged: boolean;
      latitude?: number;
      longitude?: number;
      notes?: string;
      photos: Array<{
        dataUrl: string;
        latitude: number;
        longitude: number;
        captureTime: string;
        documentType: string;
        name?: string;
        notes?: string;
      }>;
      documentIds?: string[];
    }
  ): Promise<{
    success: boolean;
    data: {
      id: string;
      dispatchId: string;
      inspectionType: string;
      vehicleDamaged: boolean;
      takenBy: string;
      notes?: string;
      documentIds: string[];
      mediaUrls?: string[];
      urgentlyReportId?: string;
      urgentlyStatus?: string;
      submittedAt?: string;
      createdAt: string;
    };
    message?: string;
  }> {
    try {
      const response = await this.client.post(
        `/dispatches/${dispatchId}/urgently/inspection-report`,
        data
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || 'Failed to submit inspection report'
      );
    }
  }
}

// Export a singleton instance
export const driverApi = new DriverApiService();
